/**
 * EmailCompose — compose drawer rendered as a Preact overlay in the reading pane.
 *
 * For new compose, it opens as a full reading-pane-width form.
 * For reply/forward, it opens as a sticky form at the bottom of the reading pane.
 *
 * TipTap is initialised via the email-tiptap.js module after the textarea mounts.
 * Address fields use bootstrap5-tags (same as Hotwire compose pane).
 * Autosave uses the email-autosave.js module — saves every 5s if content changed.
 * When compose is opened from a draft card, composeDraftData pre-fills the form.
 */

import { h, Fragment } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import Tags from 'bootstrap5-tags'
import {
  buildComposeNewPath,
  buildComposeReplyPath,
  buildComposeReplyAllPath,
  buildComposeForwardPath,
  buildEmailApiPath,
  buildMailboxSignaturePath,
  buildSidebarCountsPath,
} from '@tshmanager/email-shared'
import {
  initTipTap,
  removeTipTap,
  syncTipTap,
  getTipTapContent,
  flushTipTapUploads,
  htmlHasBlobImages,
  getTipTapEditor,
  normalizeTipTapHtml,
} from '../../modules/email/email-tiptap.js'
import { EmailAutosave } from '../../modules/email/email-autosave.js'
import { apiGet, apiPostForm, emailApiBase, emailApiHeaders } from '../../modules/email/store/api.js'
import { RequiredAsterisk } from '../shared/RequiredAsterisk.jsx'
import {
  QuotaExceededModal,
  useQuotaGate,
  parseQuotaError,
  normalizeQuota,
  isBlockingMode,
} from '../shared/quota/index.js'

function isHardStopExhausted(rawQuota) {
  const q = normalizeQuota(rawQuota)
  if (!q?.exceeded) return false
  return isBlockingMode(q.barrierMode)
}

/** POST target for a compose type — all four live under /email/v1/compose. */
function composeSendPath(composeType) {
  const base = emailApiBase()
  switch (composeType) {
    case 'reply':     return buildComposeReplyPath(base)
    case 'reply_all': return buildComposeReplyAllPath(base)
    case 'forward':   return buildComposeForwardPath(base)
    default:          return buildComposeNewPath(base)
  }
}

function addressOptions(addresses) {
  return (addresses || []).map(a => {
    const addr = typeof a === 'string' ? a : (a?.address || a?.to_s || String(a || ''))
    return addr
  }).filter(Boolean)
}

function selectedAddresses(selectEl) {
  if (!selectEl) return []
  return [...selectEl.selectedOptions].map(o => o.value).filter(Boolean)
}

/**
 * bootstrap5-tags does its own fetching, so it needs the v1 identity headers and
 * cross-subdomain credentials handed to it. The endpoint answers
 * `[{ email, name }]`, hence the value/label field overrides.
 */
function initAddressTags(selectEl) {
  if (!selectEl?.id) return
  Tags.init(`#${selectEl.id}`, {
    allowClear: true,
    showDropIcon: false,
    allowNew: selectEl.dataset.allowNew === 'true',
    addOnBlur: selectEl.dataset.addOnBlur === 'true',
    liveServer: true,
    server: buildEmailApiPath(emailApiBase(), 'compose', 'addresses'),
    queryParam: 'q',
    valueField: 'email',
    labelField: 'email',
    fetchOptions: {
      credentials: 'include',
      headers: emailApiHeaders(),
    },
  })
}

function disposeAddressTags(selectEl) {
  if (!selectEl) return
  Tags.getInstance(selectEl)?.dispose()
}

/** Ensure signature HTML has a single `.email-signature` root for swap/replace. */
function wrapSignatureHtml(sigHtml) {
  const normalized = normalizeTipTapHtml(sigHtml || '').trim()
  if (!normalized) return ''
  if (/class=["'][^"']*\bemail-signature\b/.test(normalized)) return normalized
  return `<div class="email-signature">${normalized}</div>`
}

/**
 * Anchor to insert the signature before (reply/forward quote block).
 * TipTap StarterKit strips blockquote[type=cite], so fall back to bare blockquote
 * and prefer the attribution DIV immediately before it (#348).
 */
function findComposeQuoteAnchor(body) {
  const marked = body.querySelector('.email-quoted-content')
  if (marked) return marked

  const blockquote =
    body.querySelector('blockquote[type="cite"]') ||
    body.querySelector('blockquote')
  if (!blockquote) return null

  const prev = blockquote.previousElementSibling
  if (
    prev &&
    prev.tagName === 'DIV' &&
    !prev.classList.contains('email-signature')
  ) {
    return prev
  }
  return blockquote
}

function swapMailboxSignature(editorId, sigHtml) {
  const editor = getTipTapEditor(editorId)
  if (!editor) return

  const body = editor.view.dom
  if (!body) return

  const existing = body.querySelector('.email-signature')
  const wrapped = wrapSignatureHtml(sigHtml)
  if (wrapped) {
    const tmp = document.createElement('div')
    tmp.innerHTML = wrapped
    const sigEl = tmp.querySelector('.email-signature') || tmp.firstElementChild
    if (!sigEl) return
    if (existing) {
      existing.replaceWith(sigEl)
    } else {
      const quote = findComposeQuoteAnchor(body)
      if (quote) body.insertBefore(sigEl, quote)
      else body.appendChild(sigEl)
    }
  } else if (existing) {
    existing.remove()
  }
  // DOM mutation bypasses TipTap transactions — force sync from current DOM HTML.
  editor.commands.setContent(body.innerHTML, { emitUpdate: false })
  syncTipTap(editorId)
}

export function EmailCompose({ store, i18n, mailboxes = [] }) {
  const composeOpen  = store.composeOpen.value
  const composeType  = store.composeType.value
  const draftId      = store.draftId.value
  const originalId   = store.composeOriginalEmailId.value
  const draftData    = store.composeDraftData.value

  if (!composeOpen) return null

  return (
    <ComposeForm
      key={`${composeType}-${originalId ?? 'new'}`}
      composeType={composeType}
      draftId={draftId}
      originalId={originalId}
      draftData={draftData}
      store={store}
      i18n={i18n}
      mailboxes={mailboxes}
    />
  )
}

const isMobile = () => window.innerWidth < 768

function resolveDefaultFromKey({ draftData, store, mailboxes }) {
  if (draftData?.mailbox_id) {
    const mbId = Number(draftData.mailbox_id)
    if (mailboxes.some(mb => mb.id === mbId)) return `mailbox:${mbId}`
  }

  // New compose: follow the sidebar mailbox selection.
  if (!draftData) {
    const activeMb = store.activeMailbox.value
    if (activeMb != null && mailboxes.some(mb => mb.id === Number(activeMb))) {
      return `mailbox:${Number(activeMb)}`
    }
  }

  return mailboxes[0] ? `mailbox:${mailboxes[0].id}` : ''
}

function parseFromKey(key) {
  if (!key) return null
  if (key.startsWith('mailbox:')) return Number(key.slice('mailbox:'.length))
  // Legacy bare mailbox id
  return Number(key)
}

function ComposeForm({ composeType, draftId, originalId, draftData, store, i18n, mailboxes }) {
  const editorId  = `email_compose_editor_${composeType}_${originalId || 'new'}`
  const editorRef = useRef(null)
  const editorHostRef = useRef(null)
  const formRef   = useRef(null)
  const paneRef   = useRef(null)
  const autosaveRef = useRef(null)
  const toSelectRef  = useRef(null)
  const ccSelectRef  = useRef(null)
  const bccSelectRef = useRef(null)
  const gate = useQuotaGate()

  const [showCc,  setShowCc]  = useState(!!(draftData?.cc_addresses?.length))
  const [showBcc, setShowBcc] = useState(false)
  const [subject, setSubject] = useState(draftData?.subject || '')
  const [draftIndicator, setDraftIndicator] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState('')
  const [sendWarning, setSendWarning] = useState('')
  const [attachedNames, setAttachedNames] = useState(() =>
    (draftData?.attachments || []).map(a => a.filename || a.name).filter(Boolean)
  )
  const [fromKey, setFromKey] = useState(() =>
    resolveDefaultFromKey({ draftData, store, mailboxes })
  )

  const closeBtnRef = useRef(null)
  const discardBtnRef = useRef(null)

  const isReply   = ['reply', 'reply_all', 'forward'].includes(composeType)
  const sendUrl   = composeSendPath(composeType)
  const mobile    = isMobile()
  // Thread-only popup has a fragile %-height chain; pin compose to the viewport
  // like mobile so TipTap fitEditorHeight and the action footer stay on screen.
  const fillViewport = mobile || !!store.isThreadOnlyView?.value
  const storageQuota = store.storageQuota?.value
  const sendBlocked = isHardStopExhausted(storageQuota)

  const toAddrs  = addressOptions(draftData?.to_addresses)
  const ccAddrs  = addressOptions(draftData?.cc_addresses)
  const bccAddrs = addressOptions(draftData?.bcc_addresses)

  /** Fill the reading pane: size .tiptap-editor-shell from remaining pane space. */
  function fitEditorHeight() {
    const pane = paneRef.current
    const host = editorHostRef.current
    const shell = host?.querySelector('.tiptap-editor-shell')
    if (!pane || !host || !shell) return

    // Prefer the real layout height; for fixed 100dvh panes fall back to visual viewport
    // so we never size the editor larger than the window (footer would slip off-screen).
    const paneH = pane.clientHeight || Math.floor(window.innerHeight)

    let used = 0
    for (const child of pane.children) {
      if (child === formRef.current) continue
      used += child.getBoundingClientRect().height
    }
    const form = formRef.current
    if (form) {
      for (const child of form.children) {
        if (child === host) continue
        used += child.getBoundingClientRect().height
      }
    }

    const hostStyle = getComputedStyle(host)
    const padY = (parseFloat(hostStyle.paddingTop) || 0)
      + (parseFloat(hostStyle.paddingBottom) || 0)
    const shellStyle = getComputedStyle(shell)
    const borderY = (parseFloat(shellStyle.borderTopWidth) || 0)
      + (parseFloat(shellStyle.borderBottomWidth) || 0)
    const available = Math.floor(paneH - used - padY)
    const h = Math.max(120, available - Math.ceil(borderY))

    shell.style.boxSizing = 'border-box'
    shell.style.height = `${h}px`
    shell.style.maxHeight = `${h}px`

    const toolbar = shell.querySelector('.tiptap-toolbar')
    const content = shell.querySelector('.tiptap-editor-content')
    const editH = Math.max(80, h - (toolbar?.offsetHeight || 0))
    if (content) {
      content.style.height = `${editH}px`
      content.style.maxHeight = `${editH}px`
      content.style.flex = '1 1 auto'
      content.style.minHeight = '0'
      content.style.overflow = 'auto'
    }
  }

  useEffect(() => {
    if (!editorRef.current) return

    const rawBody = draftData?.body_html || ''
    const isReplyFlow = ['reply', 'reply_all', 'forward'].includes(composeType)
    // Ensure an empty paragraph above the quote so typing does not stick to the last quote line.
    const initialBody = isReplyFlow && rawBody && !/^\s*<p>(\s|&nbsp;|<br)/i.test(rawBody)
      ? `<p><br></p>${rawBody}`
      : rawBody
    // Prefer viewport-based starting height when the pane is pinned to 100dvh
    // (mobile + thread-only popup); fitEditorHeight refines after TipTap mounts.
    const editorHeight = fillViewport
      ? Math.max(120, window.innerHeight - 280)
      : 400

    let resizeObserver = null
    let onWinResize = null
    let cancelled = false

    initTipTap(`#${editorId}`, {
      height: editorHeight,
      placeholder: i18n?.t('emails.reply_placeholder'),
      initialContent: normalizeTipTapHtml(initialBody),
      placeCaretAtStart: isReplyFlow,
      onInput: () => { store.markComposeDirty(); autosaveRef.current?.notifyChange() },
    }).then(async () => {
      if (cancelled) return
      const runFit = () => fitEditorHeight()
      runFit()
      requestAnimationFrame(() => requestAnimationFrame(runFit))

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(runFit)
        if (paneRef.current) resizeObserver.observe(paneRef.current)
        if (editorHostRef.current) resizeObserver.observe(editorHostRef.current)
      }
      onWinResize = runFit
      window.addEventListener('resize', onWinResize)

      // Load mailbox signature on open (#337). Skip saved drafts (body already stored).
      const alreadyHasSig = /email-signature/.test(initialBody)
      const mailboxId = parseFromKey(fromKey)
      if (!draftId && mailboxId && !alreadyHasSig) {
        try {
          const data = await apiGet(buildMailboxSignaturePath(emailApiBase(), mailboxId))
          if (cancelled) return
          swapMailboxSignature(editorId, data?.html || '')
          store.markComposeDirty()
          autosaveRef.current?.notifyChange()
        } catch (err) {
          console.error('[EmailCompose] signature load failed', err)
        }
      }
    })

    // Address autocomplete (bootstrap5-tags)
    ;[toSelectRef.current, ccSelectRef.current, bccSelectRef.current].forEach(initAddressTags)

    autosaveRef.current = new EmailAutosave({
      store,
      initialBodyHtml: initialBody,
      getPayload: () => {
        syncTipTap(editorId)
        const form = formRef.current
        if (!form) return null
        const bodyHtml = getTipTapContent(editorId) || form.querySelector(`textarea#${editorId}`)?.value || ''
        const subjectV = form.querySelector('[name="email[subject]"]')?.value || ''
        const mailboxId = form.querySelector('[name="email[mailbox_id]"]')?.value || null
        return {
          email_draft: {
            draft_type:       composeType === 'reply_all' ? 'reply' : (composeType || 'new'),
            body:             bodyHtml,
            subject:          subjectV,
            mailbox_id:       mailboxId,
            reply_to_email_id: originalId || null,
            to_addresses:     selectedAddresses(toSelectRef.current),
            cc_addresses:     selectedAddresses(ccSelectRef.current),
            bcc_addresses:    selectedAddresses(bccSelectRef.current),
          }
        }
      },
      onSaved: () => {
        const now = new Date()
        const hh  = String(now.getHours()).padStart(2, '0')
        const mm  = String(now.getMinutes()).padStart(2, '0')
        setDraftIndicator(
          i18n?.t('emails.draft_saved') + ` ${hh}:${mm}`
        )
      },
      onCreated: (_id) => {
        setDraftIndicator(i18n?.t('emails.draft_saved'))
      },
    })
    autosaveRef.current.start()

    async function onClose() {
      // Snapshot + persist before tearing down TipTap / closing compose.
      await autosaveRef.current?.saveNow()
      autosaveRef.current?.stop()
      store.closeCompose()
      if (originalId) store.fetchThread(originalId)
    }

    function onDiscard() {
      if (store.composeDirty.value || store.draftId.value) {
        if (!confirm(i18n?.t('emails.discard_confirm'))) return
      }
      autosaveRef.current?.deleteDraft()
      autosaveRef.current?.stop()
      store.closeCompose()
      if (originalId) store.fetchThread(originalId)
    }
    const closeBtnEl   = closeBtnRef.current
    const discardBtnEl = discardBtnRef.current
    closeBtnEl?.addEventListener('click', onClose)
    discardBtnEl?.addEventListener('click', onDiscard)

    function onAddressChange() {
      store.markComposeDirty()
      autosaveRef.current?.notifyChange()
    }
    ;[toSelectRef.current, ccSelectRef.current, bccSelectRef.current].forEach(el => {
      el?.addEventListener('change', onAddressChange)
    })

    // Keep session alive while compose stays open (long-lived tab).
    const keepAliveId = setInterval(() => {
      apiGet(buildSidebarCountsPath(emailApiBase())).catch(() => {})
    }, 5 * 60 * 1000)

    return () => {
      cancelled = true
      clearInterval(keepAliveId)
      if (onWinResize) window.removeEventListener('resize', onWinResize)
      resizeObserver?.disconnect()
      autosaveRef.current?.stop()
      removeTipTap(`#${editorId}`)
      closeBtnEl?.removeEventListener('click', onClose)
      discardBtnEl?.removeEventListener('click', onDiscard)
      ;[toSelectRef.current, ccSelectRef.current, bccSelectRef.current].forEach(el => {
        el?.removeEventListener('change', onAddressChange)
        disposeAddressTags(el)
      })
    }
  }, [])

  // Re-fit when CC/BCC rows toggle (changes space above the editor)
  useEffect(() => {
    const id = requestAnimationFrame(() => fitEditorHeight())
    return () => cancelAnimationFrame(id)
  }, [showCc, showBcc])

  async function handleFromChange(e) {
    const key = e.target.value
    setFromKey(key)
    store.markComposeDirty()
    autosaveRef.current?.notifyChange()

    const mailboxId = parseFromKey(key)
    if (!mailboxId) return
    try {
      const data = await apiGet(buildMailboxSignaturePath(emailApiBase(), mailboxId))
      swapMailboxSignature(editorId, data?.html || '')
      store.markComposeDirty()
      autosaveRef.current?.notifyChange()
    } catch (err) {
      console.error('[EmailCompose] signature swap on From change failed', err)
    }
  }

  function translateSendError(key, fallback) {
    const msg = i18n?.t(key, { defaultValue: fallback })
    if (!msg || /^\[missing /.test(msg)) return fallback
    return msg
  }

  // Close compose when email-jobs SES handoff confirms via ActionCable.
  const composeSendOutcome = store.composeSendOutcome?.value
  useEffect(() => {
    if (!composeSendOutcome) return
    const { type, email_id: emailId } = composeSendOutcome
    const pending = store.pendingSendEmailId?.value
    if (pending == null || Number(emailId) !== Number(pending)) return

    const originalId = store.composeOriginalEmailId?.value

    ;(async () => {
      if (type === 'email_sent') {
        await autosaveRef.current?.deleteDraft()
        store.clearComposeSendOutcome?.()
        store.closeCompose()
        store.fetchEmails(1)
        if (originalId) store.fetchThread(originalId)
        return
      }
      if (type === 'email_send_failed') {
        setSending(false)
        autosaveRef.current?.start()
        setSendWarning('')
        setSendError(translateSendError(
          'emails.send_error',
          'A levél küldése nem sikerült. Kérjük, próbálja újra.'
        ))
        store.setPendingSendEmailId?.(null)
        store.clearComposeSendOutcome?.()
      }
    })()
  }, [composeSendOutcome])

  async function handleSubmit(e) {
    e.preventDefault()
    if (sending) return
    if (sendBlocked) {
      if (storageQuota) gate.captureBody(storageQuota, 402)
      return
    }
    setSending(true)
    setSendError('')

    autosaveRef.current?.stop()
    let bodyHtml = ''
    try {
      bodyHtml = await flushTipTapUploads(editorId)
    } catch (_) {
      bodyHtml = ''
    }
    if (htmlHasBlobImages(bodyHtml)) {
      setSending(false)
      autosaveRef.current?.start()
      setSendError(translateSendError(
        'emails.images_still_uploading',
        'A képek feltöltése még nem fejeződött be. Várjon egy pillanatot, majd próbálja újra.'
      ))
      return
    }
    const form = formRef.current
    const fd = new FormData(form)
    fd.set('email[body_html]', bodyHtml)

    // Ensure address arrays come from tag selects (selected options)
    fd.delete('email[to_addresses][]')
    fd.delete('email[cc_addresses][]')
    fd.delete('email[bcc_addresses][]')
    selectedAddresses(toSelectRef.current).forEach(a => fd.append('email[to_addresses][]', a))
    selectedAddresses(ccSelectRef.current).forEach(a => fd.append('email[cc_addresses][]', a))
    selectedAddresses(bccSelectRef.current).forEach(a => fd.append('email[bcc_addresses][]', a))

    try {
      const res = await apiPostForm(sendUrl, fd)
      if (res.ok) {
        // Only delete the local draft once we're confident the message was
        // actually handed off to SES (not just persisted+enqueued). A real
        // SES message_id contains "amazonses"; delivery_status sent/delivered
        // confirms it further. This avoids losing the draft if deliver_later's
        // background job later fails (e.g. SesV2TenantMailer#deliver! raising),
        // which marks delivery_status: 'failed' asynchronously after this
        // response already returned 201.
        const messageId = res.data?.message_id || ''
        const deliveryStatus = res.data?.delivery_status || ''
        const confirmedSent = messageId.includes('amazonses') ||
          ['sent', 'delivered'].includes(deliveryStatus)

        if (confirmedSent) {
          await autosaveRef.current?.deleteDraft()
          store.closeCompose()
          store.fetchEmails(1)
          if (originalId) store.fetchThread(originalId)
        } else {
          // Async handoff not confirmed yet (pending-*@outbound.invalid).
          // Keep compose open until EmailJsonChannel email_sent / email_send_failed.
          store.setPendingSendEmailId?.(res.data?.id)
          setSending(false)
          autosaveRef.current?.start()
          setSendError('')
          setSendWarning(translateSendError(
            'emails.send_queued_unconfirmed',
            'The message was saved, but delivery is still in progress. Your draft is kept until send is confirmed.'
          ))
          store.fetchEmails(1)
          if (originalId) store.fetchThread(originalId)
        }
      } else {
        setSending(false)
        // Sending failed — the draft is still intact server-side; resume
        // autosave so further edits (or a retry) keep saving.
        autosaveRef.current?.start()
        const quota = parseQuotaError(res.data, res.status)
        if (quota) {
          gate.captureBody(res.data, res.status)
          return
        }
        let msg = translateSendError(
          'emails.send_error',
          'A levél küldése nem sikerült. Kérjük, próbálja újra.'
        )
        if (res.status === 401 || res.status === 403) {
          msg = translateSendError(
            'emails.send_session_expired',
            'A munkamenet lejárt. Töltse újra az oldalt, majd próbálja újra.'
          )
        } else if (res.data?.errors?.length) {
          msg = res.data.errors.join(', ')
        } else if (res.data?.error) {
          msg = res.data.error
        }
        setSendError(msg)
        console.error('[EmailCompose] send failed', res.status)
      }
    } catch (err) {
      setSending(false)
      // Network/unexpected error — resume autosave so edits are not lost.
      autosaveRef.current?.start()
      setSendError(translateSendError(
        'emails.send_error',
        'A levél küldése nem sikerült. Kérjük, próbálja újra.'
      ))
      console.error('[EmailCompose] send error', err)
    }
  }

  const title = composeType === 'forward' ? i18n?.t('emails.forward')
              : composeType === 'reply_all' ? i18n?.t('emails.reply_all')
              : composeType === 'reply' ? i18n?.t('emails.reply')
              : i18n?.t('emails.compose')

  function handleDragOver(e) {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    }
  }

  function handleDrop(e) {
    if (!e.dataTransfer.files?.length) return
    e.preventDefault()
    const input = formRef.current?.querySelector('input[type="file"][name="email[files][]"]')
    if (!input) return
    const dt = new DataTransfer()
    ;[...(input.files || []), ...e.dataTransfer.files].forEach(f => dt.items.add(f))
    input.files = dt.files
    setAttachedNames([...dt.files].map(f => f.name))
    store.markComposeDirty()
  }

  function handleAttachChange(e) {
    const files = [...(e.target.files || [])]
    setAttachedNames(files.map(f => f.name))
    store.markComposeDirty()
  }

  function removeAttachedFile(name) {
    const input = formRef.current?.querySelector('input[type="file"][name="email[files][]"]')
    if (!input) return
    const dt = new DataTransfer()
    ;[...(input.files || [])].filter(f => f.name !== name).forEach(f => dt.items.add(f))
    input.files = dt.files
    setAttachedNames([...dt.files].map(f => f.name))
    store.markComposeDirty()
  }

  return (
    <>
    <div
      ref={paneRef}
      id="email_compose_pane"
      class="d-flex flex-column email-compose-pane"
      style={fillViewport
        ? 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:1070;background:var(--bs-body-bg);height:100dvh;max-height:100dvh'
        : 'height:100%;max-height:100%;position:relative'}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div class="d-flex align-items-center px-3 py-2 border-bottom bg-body-tertiary flex-shrink-0">
        <h6 class="mb-0 fw-semibold flex-grow-1">
          <i class={`bi ${composeType === 'new' ? 'bi-pencil-square' : 'bi-reply'} me-1`} />
          {title}
        </h6>
        {draftIndicator && (
          <span class="text-muted small me-2 d-flex align-items-center gap-1">
            <i class="bi bi-check2 text-success" />
            {draftIndicator}
          </span>
        )}
        <button ref={closeBtnRef} type="button" class="btn btn-sm btn-link text-muted p-0" title={i18n?.t('emails.discard')}>
          <i class="bi bi-x-lg" />
        </button>
      </div>

      {sendError && (
        <div class="alert alert-danger email-compose-send-error d-flex align-items-center gap-2 mx-3 mt-2 py-2 small mb-0" role="alert">
          <i class="bi bi-exclamation-triangle-fill flex-shrink-0" aria-hidden="true" />
          <span class="flex-grow-1">{sendError}</span>
          <button type="button" class="btn-close flex-shrink-0" onClick={() => setSendError('')} aria-label="Close" />
        </div>
      )}
      {sendWarning && (
        <div class="alert alert-warning email-compose-send-warning d-flex align-items-center gap-2 mx-3 mt-2 py-2 small mb-0" role="status">
          <i class="bi bi-hourglass-split flex-shrink-0" aria-hidden="true" />
          <span class="flex-grow-1">{sendWarning}</span>
          <button type="button" class="btn-close flex-shrink-0" onClick={() => setSendWarning('')} aria-label="Close" />
        </div>
      )}

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        encType="multipart/form-data"
        class="d-flex flex-column flex-grow-1 overflow-hidden email-compose-form min-h-0"
        style="min-height:0"      >
        {originalId && <input type="hidden" name="email_id" value={originalId} />}
        {draftId    && <input type="hidden" name="draft_id" value={draftId} />}

        {mailboxes.length > 0 && (() => {
          const mailboxId = parseFromKey(fromKey)
          return (
            <div class="d-flex align-items-center border-bottom px-3 py-1">
              <span class="text-muted small email-field-label me-2">{i18n?.t('emails.from')}</span>
              <input type="hidden" name="email[mailbox_id]" value={mailboxId || ''} />
              <select
                class="form-select form-select-sm border-0 shadow-none ps-0 flex-grow-1"
                value={fromKey}
                onChange={handleFromChange}
              >
                {mailboxes.map(mb => (
                  <option key={`mailbox:${mb.id}`} value={`mailbox:${mb.id}`}>
                    {mb.name}
                  </option>
                ))}
              </select>
            </div>
          )
        })()}

        {/* To */}
        <div class="d-flex align-items-center border-bottom px-3 py-1">
          <span class="text-muted small email-field-label me-2">
            {i18n?.t('emails.to')}
            <RequiredAsterisk />
          </span>
          <div class="flex-grow-1">
            <select
              ref={toSelectRef}
              id="email_compose_to_addresses"
              name="email[to_addresses][]"
              multiple
              class="form-select border-0 shadow-none"
              data-allow-new="true"
              data-add-on-blur="true"
              required
            >
              {toAddrs.map(addr => (
                <option key={addr} value={addr} selected>{addr}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-link text-muted small p-0 flex-shrink-0"
            onClick={() => { setShowCc(!showCc); setShowBcc(!showBcc) }}
          >
            Cc/Bcc
          </button>
        </div>

        {/* CC — always mounted so Tags can init; visibility toggled */}
        <div class={`d-flex align-items-center border-bottom px-3 py-1 ${showCc ? '' : 'd-none'}`}>
          <span class="text-muted small email-field-label me-2">{i18n?.t('emails.cc')}</span>
          <div class="flex-grow-1">
            <select
              ref={ccSelectRef}
              id="email_compose_cc_addresses"
              name="email[cc_addresses][]"
              multiple
              class="form-select border-0 shadow-none"
              data-allow-new="true"
              data-add-on-blur="true"
            >
              {ccAddrs.map(addr => (
                <option key={addr} value={addr} selected>{addr}</option>
              ))}
            </select>
          </div>
        </div>

        {/* BCC */}
        <div class={`d-flex align-items-center border-bottom px-3 py-1 ${showBcc ? '' : 'd-none'}`}>
          <span class="text-muted small email-field-label me-2">{i18n?.t('emails.bcc')}</span>
          <div class="flex-grow-1">
            <select
              ref={bccSelectRef}
              id="email_compose_bcc_addresses"
              name="email[bcc_addresses][]"
              multiple
              class="form-select border-0 shadow-none"
              data-allow-new="true"
              data-add-on-blur="true"
            >
              {bccAddrs.map(addr => (
                <option key={addr} value={addr} selected>{addr}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Subject */}
        <div class="border-bottom px-3 py-1">
          <input
            type="text"
            name="email[subject]"
            class="form-control border-0 shadow-none px-0 fw-medium"
            placeholder={i18n?.t('emails.subject_placeholder')}
            value={subject}
            onInput={e => { setSubject(e.target.value); store.markComposeDirty(); autosaveRef.current?.notifyChange() }}
          />
        </div>

        {/* TipTap body — flex host fills remaining pane; ResizeObserver sizes shell */}
        <div ref={editorHostRef} class="email-compose-editor-host flex-grow-1 px-3 py-2" style="min-height:0">
          <textarea
            ref={editorRef}
            id={editorId}
            name="email[body_html]"
            class="tiptap-source"
            rows={8}
            style="width:100%"
            onInput={() => store.markComposeDirty()}
          />
        </div>

        {/* Retain attachments (reply/forward) */}
        {isReply && (
          <div class="px-3 py-1 border-top flex-shrink-0">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                name="retain_attachments"
                value="true"
                id="email_compose_retain_attachments"
              />
              <label class="form-check-label small" for="email_compose_retain_attachments">
                {i18n?.t('emails.retained_attachments')}
              </label>
            </div>
          </div>
        )}
        {/* Footer — left tools + send; desktop padding clears the support beacon FAB */}
        <div class="email-compose-actions flex-shrink-0">
          <div class="email-compose-actions-main">
            <label class="btn btn-sm btn-outline-secondary mb-0" title={i18n?.t('emails.attach_file')}>
              <i class="bi bi-paperclip" />
              <input
                type="file"
                name="email[files][]"
                multiple
                class="d-none"
                onInput={handleAttachChange}
              />
            </label>

            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              title={i18n?.t('emails.save_draft')}
              onClick={() => autosaveRef.current?.saveNow()}
            >
              <i class="bi bi-floppy" />
            </button>

            {attachedNames.length > 0 && (
              <div class="email-compose-actions-files">
                {attachedNames.map(name => (
                  <span key={name} class="badge bg-secondary-subtle text-dark border d-flex align-items-center gap-1 p-1">
                    <i class="bi bi-paperclip small" />
                    {name}
                    <button
                      type="button"
                      class="btn-close btn-close-dark ms-1"
                      style="font-size:8px"
                      onClick={() => removeAttachedFile(name)}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          <div class="email-compose-actions-send">
            <button
              ref={discardBtnRef}
              type="button"
              class="btn btn-sm btn-outline-secondary email-compose-discard"
              title={i18n?.t('emails.discard')}
              aria-label={i18n?.t('emails.discard')}
            >
              <i class="bi bi-trash3" />
            </button>
            <button
              type={sendBlocked ? 'button' : 'submit'}
              class={`btn btn-primary btn-sm d-flex align-items-center gap-1${sendBlocked ? ' disabled' : ''}`}
              disabled={sending}
              aria-disabled={sendBlocked ? 'true' : undefined}
              onClick={sendBlocked ? (e) => {
                e.preventDefault()
                if (storageQuota) gate.captureBody(storageQuota, 402)
              } : undefined}
            >
              {sending
                ? <span class="spinner-border spinner-border-sm me-1" role="status" />
                : <i class={`bi ${sendBlocked ? 'bi-lock-fill' : 'bi-send'}`} />
              }
              {i18n?.t('emails.send')}
            </button>
          </div>
        </div>

      </form>
    </div>
    <QuotaExceededModal
      open={gate.open}
      quota={gate.quota}
      t={(key, opts) => i18n?.t(key, opts)}
      format="bytes"
      onClose={gate.dismiss}
    />
    </>
  )
}
